import { Box, Skeleton, Typography, alpha } from '@mui/material';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import palette from '../layout/ColorPalette';
import { RTLDirectionContext } from '../layout/Layout';
import { LayoutOptions } from '../layout/content/Event/EventVideoStreams';
import { VIDEO_CAMERA_ORDER, VideoStreamType, detectStreamCount, geussStream } from '../utils/ImageDimensions';

import { getSubImageDataUrl, type ImageMetadata } from './getSubImageDataUrl';

/** Delay between failed sprite sub-image fetch attempts (ms). Exported for tests with fake timers. */
export const SUB_IMAGE_RETRY_DELAY_MS = 3000;

interface SubImageProps {
    id?: string;
    imageType?: string;
    spriteUrl: string;
    subImageData: ImageMetadata;
    attempts?: number;
    onError: CallableFunction;
    testId?: string;
    imageStyle?: React.CSSProperties;
}

export const SubImage: React.FC<SubImageProps> = (props) => {
    const { imageType, spriteUrl, subImageData, onError, testId, attempts: attemptsProp } = props;
    const maxAttempts = attemptsProp ?? 5;
    const [dataUrl, setDataUrl] = useState<string | null>(null);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    useEffect(() => {
        let cancelled = false;
        setDataUrl(null);

        (async () => {
            for (let i = 0; i < maxAttempts; i++) {
                try {
                    const url = await getSubImageDataUrl(spriteUrl, subImageData);
                    if (!cancelled) {
                        setDataUrl(url);
                    }
                    return;
                } catch {
                    if (cancelled) {
                        return;
                    }
                    if (i === maxAttempts - 1) {
                        onErrorRef.current();
                    } else {
                        await new Promise((resolve) => setTimeout(resolve, SUB_IMAGE_RETRY_DELAY_MS));
                        if (cancelled) {
                            return;
                        }
                    }
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [spriteUrl, ...subImageData, maxAttempts]);

    let content;
    if (!dataUrl) {
        content = <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />;
    } else {
        content = (
            <img
                id={props.id ?? 'sub-image'}
                data-image-type={imageType}
                src={dataUrl}
                style={{ height: '100%', width: '100%', display: 'block' }}
            />
        );
    }

    return (
        <div
            data-testid={testId}
            key={spriteUrl}
            style={props.imageStyle ?? { width: '100%', aspectRatio: `${subImageData[2]} / ${subImageData[3]}` }}
        >
            {content}
        </div>
    );
};

interface StreamOverlayProps {
    streamName: VideoStreamType;
    fontSize?: number;
}

// Reusable overlay component for camera/stream labels
const StreamOverlay: React.FC<StreamOverlayProps> = ({ streamName, fontSize = 12 }) => {
    const { t } = useTranslation();
    const isRTL = useContext(RTLDirectionContext);

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 5,
                insetInlineEnd: 5,
                backgroundColor: alpha(palette.black, 0.3),
                py: 0.5,
                px: 1,
                zIndex: 1200,
                display: 'flex',
                flexDirection: 'column',
                alignItems: isRTL ? 'flex-end' : 'flex-start',
                borderRadius: 2,
            }}
        >
            <Typography sx={{ fontSize, color: palette.white }}>{t(`content.cameras.${streamName}`)}</Typography>
        </Box>
    );
};

interface MultipleSubImageProps {
    id?: string;
    image: string;
    containerStyle?: React.CSSProperties;
    streams?: VideoStreamType[]; // Made optional - will auto-detect if not provided
    primaryStream?: VideoStreamType | null; // Controlled primary stream (for synchronized segments)
    onPrimaryStreamChange?: (stream: VideoStreamType) => void; // Callback when primary stream changes
    showOverlays?: boolean; // Whether to show camera name overlays (default: false)
    enableStreamSwapping?: boolean; // Whether to allow clicking secondary images to swap main stream (default: false)
}
export const MultipleSubImage: React.FC<MultipleSubImageProps> = (props) => {
    const [ImageDimensions, setImageDimensions] = useState<[number, number]>();
    const [streamCount, setStreamCount] = useState<number>(2); // Default to 2 for backward compatibility
    const [viewMode, setViewMode] = useState<LayoutOptions>('2x1');
    const [internalPrimaryStream, setInternalPrimaryStream] = useState<VideoStreamType | null>(null); // Internal state for uncontrolled mode
    const isRTL = useContext(RTLDirectionContext);
    const showOverlays = props.showOverlays ?? false;
    const enableStreamSwapping = props.enableStreamSwapping ?? false;

    // Use controlled primaryStream if provided, otherwise use internal state
    const primaryStream = props.primaryStream !== undefined ? props.primaryStream : internalPrimaryStream;
    const setPrimaryStream = props.onPrimaryStreamChange || setInternalPrimaryStream;

    useEffect(() => {
        if (props.image) {
            const img = new Image();
            img.src = props.image;
            img.onload = () => {
                const width = img.naturalWidth;
                const height = img.naturalHeight;
                setImageDimensions([width, height]);

                // Detect stream count from dimensions
                const detectedCount = detectStreamCount(width, height);
                setStreamCount(detectedCount);

                // Determine view mode based on stream count
                if (detectedCount === 1) {
                    setViewMode('1x1');
                } else if (detectedCount === 2) {
                    setViewMode('2x1');
                } else if (detectedCount === 3 || detectedCount === 4) {
                    setViewMode('1x1+3x1');
                } else {
                    // Fallback to 2x1 if unknown
                    setViewMode('2x1');
                }
            };
        }
    }, [props.image]);

    // Determine which streams to display based on VIDEO_CAMERA_ORDER
    const baseStreamsToDisplay = useMemo((): VideoStreamType[] => {
        // If streams prop is provided, use it (backward compatibility)
        if (props.streams && props.streams.length > 0) {
            return props.streams.slice(0, streamCount);
        }

        // Otherwise, use VIDEO_CAMERA_ORDER based on detected stream count
        // Default to 2 streams if streamCount is not between 1-4
        const count = streamCount >= 1 && streamCount <= 4 ? streamCount : 2;
        return VIDEO_CAMERA_ORDER.slice(0, count);
    }, [props.streams, streamCount]);

    // Initialize primary stream on first load or when streams change (only for uncontrolled mode)
    useEffect(() => {
        if (props.primaryStream === undefined && baseStreamsToDisplay.length > 0) {
            if (!internalPrimaryStream || !baseStreamsToDisplay.includes(internalPrimaryStream)) {
                setInternalPrimaryStream(baseStreamsToDisplay[0]);
            }
        }
    }, [baseStreamsToDisplay, internalPrimaryStream, props.primaryStream]);

    // Reorder streams so the primary stream is first
    const streamsToDisplay = React.useMemo(() => {
        if (baseStreamsToDisplay.length <= 1) {
            return baseStreamsToDisplay;
        }
        if (!primaryStream || !baseStreamsToDisplay.includes(primaryStream)) {
            return baseStreamsToDisplay;
        }
        const primary = primaryStream;
        const others = baseStreamsToDisplay.filter((stream) => stream !== primary);
        return [primary, ...others];
    }, [baseStreamsToDisplay, primaryStream]);

    if (!ImageDimensions) {
        return (
            <Skeleton
                id={`${streamsToDisplay[0]}-${props.id ?? 'sub-image'}`}
                data-testid={`${streamsToDisplay[0]}-${props.id ?? 'sub-image'}`}
                variant="rectangular"
                animation="wave"
                sx={props.containerStyle}
            />
        );
    }

    // 1x1 view mode - single image
    if (viewMode === '1x1' || props.streams?.length === 1) {
        return (
            <Box
                id={props.id}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    ...props.containerStyle,
                }}
            >
                {props.streams?.length === 1 ? (
                    <SubImage
                        id={`${streamsToDisplay[0]}-${props.id ?? 'sub-image'}`}
                        testId={`${streamsToDisplay[0]}-${props.id ?? 'sub-image'}`}
                        spriteUrl={props.image}
                        subImageData={geussStream(streamsToDisplay[0], ImageDimensions[0], ImageDimensions[1])}
                        onError={() => null}
                        imageStyle={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                ) : (
                    <img
                        id={`${streamsToDisplay[0]}-${props.id ?? 'sub-image'}`}
                        data-testid={`${streamsToDisplay[0]}-${props.id ?? 'sub-image'}`}
                        src={props.image}
                        style={{
                            width: '100%',
                            height: '100%',
                            aspectRatio: `${ImageDimensions[0]} / ${ImageDimensions[1]}`,
                            display: 'block',
                            objectFit: 'contain',
                        }}
                    />
                )}
                {showOverlays && <StreamOverlay streamName={streamsToDisplay[0]} />}
            </Box>
        );
    }

    // 2x1 view mode - two images side by side
    // Always show InCabinCamera on left, FrontFacingCamera on right (regardless of RTL/LTR)
    if (viewMode === '2x1' && baseStreamsToDisplay.length >= 2) {
        // Ensure order is always [InCabinCamera, FrontFacingCamera] for 2x1 mode
        const orderedStreams = baseStreamsToDisplay.slice(0, 2);
        const inCabinStream = orderedStreams.find((s) => s === VideoStreamType.InCabinCamera) || orderedStreams[0];
        const frontFacingStream =
            orderedStreams.find((s) => s === VideoStreamType.FrontFacingCamera) ||
            orderedStreams.find((s) => s !== inCabinStream) ||
            orderedStreams[1];
        // Ensure we have exactly 2 distinct streams: [InCabinCamera, FrontFacingCamera]
        const twoStreams =
            inCabinStream === frontFacingStream
                ? orderedStreams.slice(0, 2) // Fallback to original order if same stream
                : [inCabinStream, frontFacingStream].filter((s) => s !== undefined);

        return (
            <Box
                id={props.id}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center', // Ensure both images have same height
                    flexDirection: isRTL ? 'row-reverse' : 'row', // Always row (no RTL reversal) - FF always on right
                    width: '100%',
                    height: '100%',
                    ...props.containerStyle,
                }}
            >
                {twoStreams.map((stream) => {
                    return (
                        <Box
                            key={stream}
                            sx={{
                                position: 'relative',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <SubImage
                                id={`${stream}-${props.id ?? 'sub-image'}`}
                                testId={`${stream}-${props.id ?? 'sub-image'}`}
                                spriteUrl={props.image}
                                subImageData={geussStream(stream, ImageDimensions[0], ImageDimensions[1])}
                                onError={() => null}
                                imageStyle={{ display: 'block', width: '100%', height: '100%' }}
                            />
                            {showOverlays && <StreamOverlay streamName={stream} />}
                        </Box>
                    );
                })}
            </Box>
        );
    }

    // 1x1+3x1 view mode - big picture on the right, 2-3 smaller images on the left (each 1/3 height)
    if (viewMode === '1x1+3x1') {
        const mainStream = streamsToDisplay[0];
        const secondaryStreams = streamsToDisplay.slice(1);

        return (
            <Box
                id={props.id}
                sx={{
                    direction: isRTL ? 'rtl' : 'ltr',
                    display: 'grid',
                    gridTemplateRows: 'repeat(3, 1fr)', // Always 3 rows of equal height - each secondary image gets 1/3 of main video height
                    gridTemplateColumns: '1fr 3fr', // Left column for secondaries, right column for main
                    gridTemplateAreas: `"secondary0 main"
"secondary1 main"
"secondary2 main"`, // All 3 rows for 3 streams
                    width: '100%',
                    aspectRatio: '8 / 3', // Constrain container so main video (2/3 width) maintains 16:9: container height = (2/3 * width) * 9/16 = width * 3/8, so aspect ratio = 8/3
                    maxHeight: '100%', // Allow it to shrink if parent is smaller
                    gap: '2px',
                    ...props.containerStyle,
                }}
            >
                {/* Main stream - takes full height on the right */}
                <Box
                    sx={{
                        position: 'relative',
                        gridArea: 'main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <SubImage
                        id={`${mainStream}-${props.id ?? 'sub-image'}`}
                        testId={`${mainStream}-${props.id ?? 'sub-image'}`}
                        spriteUrl={props.image}
                        subImageData={geussStream(mainStream, ImageDimensions[0], ImageDimensions[1])}
                        onError={() => null}
                        imageStyle={{ display: 'block', width: '100%', height: '100%' }}
                    />
                    {showOverlays && <StreamOverlay streamName={mainStream} />}
                </Box>

                {/* Secondary streams - left column, each taking 1/3 height */}
                {secondaryStreams.map((stream, index) => (
                    <Box
                        key={stream}
                        onClick={() => {
                            if (enableStreamSwapping && stream !== primaryStream) {
                                setPrimaryStream(stream);
                            }
                        }}
                        sx={{
                            position: 'relative',
                            gridArea: `secondary${index}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            overflow: 'hidden', // Ensure images don't overflow their grid cell
                            cursor: enableStreamSwapping ? 'pointer' : 'default',
                            '&:hover': enableStreamSwapping ? { opacity: 0.8 } : {},
                        }}
                    >
                        <SubImage
                            id={`${stream}-${props.id ?? 'sub-image'}`}
                            testId={`${stream}-${props.id ?? 'sub-image'}`}
                            spriteUrl={props.image}
                            subImageData={geussStream(stream, ImageDimensions[0], ImageDimensions[1])}
                            onError={() => null}
                            imageStyle={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                        {showOverlays && <StreamOverlay streamName={stream} />}
                    </Box>
                ))}
            </Box>
        );
    }

    // Fallback to 2x1 if view mode is unknown
    return (
        <Box
            id={props.id}
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                width: '100%',
                height: '100%',
                ...props.containerStyle,
            }}
        >
            {streamsToDisplay.slice(0, 2).map((stream) => {
                return (
                    <Box
                        key={stream}
                        sx={{
                            position: 'relative',
                            width: '50%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SubImage
                            id={`${stream}-${props.id ?? 'sub-image'}`}
                            testId={`${stream}-${props.id ?? 'sub-image'}`}
                            spriteUrl={props.image}
                            subImageData={geussStream(stream, ImageDimensions[0], ImageDimensions[1])}
                            onError={() => null}
                            imageStyle={{ display: 'block', width: '100%', height: '100%' }}
                        />
                        {showOverlays && <StreamOverlay streamName={stream} />}
                    </Box>
                );
            })}
        </Box>
    );
};
